import React, { useContext } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiComment } from '@wix/da-papi-types';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import GiveBadgeIconSmall from '@wix/da-ds/pkg/Icons/16x16/GiveBadge';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import { ResourceType } from '../../../types/resource';
import { CommentContext } from '../../CommentContext';
import { useRestrictToLoggedIn } from '../../../utils/hooks/useRestrictToLoggedIn';
import { AwardBadgeSource } from '../../../coreBadge/types';
import { BiData, GiveBadgeAwardBiEvent } from '@wix/da-bi/pkg/events';

import s from './AwardBadgeButton.scss';

export interface Props {
  disabled?: boolean;
  className?: string;
  awardBadge: (comment: PapiComment, source: AwardBadgeSource) => void;
}

const AwardBadgeButton: React.FC<Props> = ({
  className,
  disabled,
  awardBadge,
}) => {
  const { t } = useTranslation();
  const { restrictToLoggedIn } = useRestrictToLoggedIn();
  const { comment, isReduxReady } = useContext(CommentContext);

  return (
    // Wrapper div because SimpleTooltip can render two elements at its root which will throw
    // off grid and flex layouts
    <div className={c(s['badge-button-wrapper'], className)}>
      <SimpleTooltip
        listenOnClicks={false}
        text={t('comment.action.badge.tooltip')}
      >
        <PlainButtonOrLink
          className={s['badge-button']}
          onClick={restrictToLoggedIn(ReferralType.AWARD_BADGE, () =>
            awardBadge(comment, AwardBadgeSource.COMMENT)
          )}
          disabled={disabled ?? !isReduxReady}
          biData={BiData<GiveBadgeAwardBiEvent>({
            evid: 320,
            sectionname: 'comments',
            typeid: ResourceType.COMMENT,
            itemid: comment.commentId,
          })}
          aria-label={t('comment.a11y.action.badge')}
        >
          <GiveBadgeIconSmall
            className={s['badge']}
            customSizable
            disableDefaultColors
            size={IconSize.SMALLER}
          />
        </PlainButtonOrLink>
      </SimpleTooltip>
    </div>
  );
};
AwardBadgeButton.displayName = 'AwardBadgeButton';

export default AwardBadgeButton;
